import React from 'react';
import { useEffect, useState } from 'react';
import { ClipboardEdit, Upload, FileStack, Lock } from './icons';
import type { IconProps } from './icons';
import type { ComponentType } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  Select,
  SelectItem,
} from './ui';
import { ManualProductWizard } from './AddProductWizard';
import { BulkCsvUploadDialog } from './BulkCsvUploadDialog';
import { EntryPacketUploadDialog } from './EntryPacketUploadDialog';
import { fetchImportDatabaseListItemsByGroupId } from './lib/import-service';
import type { ImportServiceDatabase } from './lib/import-service';
import { toast } from './lib/toast';
import { useImportHubReadOnly } from './lib/access';

type Method = 'manual' | 'csv' | 'packet';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** When omitted, the chooser requires the user to pick a database first. */
  databaseId?: string;
  /** Required to load live database choices when databaseId is omitted. */
  groupId?: string;
  /** Forwarded to ManualProductWizard — called on "View product". */
  onProductCreated?: (id: string) => void;
}

export const AddProductsDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  databaseId,
  groupId,
  onProductCreated,
}) => {
  const readOnly = useImportHubReadOnly();
  const [method, setMethod] = useState<Method | null>(null);
  const [pickedDb, setPickedDb] = useState<string>(databaseId ?? '');
  const [databases, setDatabases] = useState<ImportServiceDatabase[]>([]);
  const [databasesLoading, setDatabasesLoading] = useState(false);
  const effectiveDb = databaseId ?? pickedDb;
  const selectedDatabase = databases.find(({ id }) => id === effectiveDb);

  useEffect(() => {
    if (!open || databaseId || !groupId) return;
    let cancelled = false;
    setDatabasesLoading(true);
    fetchImportDatabaseListItemsByGroupId(groupId).then(
      ({ success, databases: nextDatabases, error }) => {
        if (cancelled) return;
        setDatabasesLoading(false);
        if (success) {
          setDatabases(nextDatabases);
        } else {
          toast.error(error ?? 'Failed to load databases');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [databaseId, groupId, open]);

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setMethod(null);
      setPickedDb(databaseId ?? '');
    }, 200);
  };

  if (method === 'manual' && effectiveDb) {
    return (
      <ManualProductWizard
        open={open}
        onOpenChange={close}
        databaseId={effectiveDb}
        databaseName={selectedDatabase?.name}
        onProductCreated={onProductCreated}
      />
    );
  }
  if (method === 'csv' && effectiveDb) {
    return (
      <BulkCsvUploadDialog
        open={open}
        onOpenChange={close}
        databaseId={effectiveDb}
      />
    );
  }
  if (method === 'packet' && effectiveDb) {
    return (
      <EntryPacketUploadDialog
        open={open}
        onOpenChange={close}
        databaseId={effectiveDb}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) close();
      }}
    >
      <DialogContent size='lg' onClose={close}>
        <DialogHeader>
          <DialogTitle>Add products</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {!databaseId && (
            <div className='ih-col ih-gap-1' style={{ marginBottom: 16 }}>
              <label className='ih-label'>Target database</label>
              <Select
                value={pickedDb}
                onValueChange={setPickedDb}
                disabled={databasesLoading}
              >
                <option value='' disabled>
                  {databasesLoading
                    ? 'Loading databases…'
                    : 'Select a database…'}
                </option>
                {databases.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </Select>
              {!databasesLoading && databases.length === 0 && (
                <span className='ih-text-xs ih-text-muted'>
                  No live product databases are available.
                </span>
              )}
            </div>
          )}

          <div className='ih-method-grid'>
            <MethodCard
              icon={ClipboardEdit}
              title='Add a single product'
              body='Enter product, classification, and trade details in one form.'
              disabled={!effectiveDb}
              locked={readOnly}
              onClick={() => setMethod('manual')}
            />
            <MethodCard
              icon={Upload}
              title='Bulk upload (CSV)'
              body='Drop a CSV, map columns, validate, and import many products at once.'
              disabled={!effectiveDb}
              locked={readOnly}
              onClick={() => setMethod('csv')}
            />
            <MethodCard
              icon={FileStack}
              title='Upload entry packet'
              body='Upload PDFs (invoice, packing list, entry summary). We extract products + create an entry.'
              disabled={!effectiveDb}
              locked={readOnly}
              onClick={() => setMethod('packet')}
            />
          </div>

          {!effectiveDb && (
            <p className='ih-text-xs ih-text-muted ih-mt-3'>
              Select a database to enable the upload methods.
            </p>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const MethodCard: React.FC<{
  icon: ComponentType<IconProps>;
  title: string;
  body: string;
  onClick: () => void;
  disabled?: boolean;
  /** Disabled due to a sub-Business license, rendered with a lock icon. */
  locked?: boolean;
}> = ({ icon: Icon, title, body, onClick, disabled, locked }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled || locked}
      title={locked ? 'Requires a Business license' : undefined}
      className='ih-method-card'
    >
      <div className='ih-method-card-icon'>
        {locked ? <Lock size={16} /> : <Icon size={16} />}
      </div>
      <div className='ih-font-semibold'>{title}</div>
      <p className='ih-text-xs ih-text-muted'>{body}</p>
    </button>
  );
};
